/**
 * Format selector component for configuring data formatting
 */

import React from 'react';
import { Label } from '@/ui-components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui-components/ui/select';
import { Settings } from 'lucide-react';

import { TextFormat, DateFormat, NumberFormat, ColorFormat, ImageFormat, MediaType } from '../../../core/template.schemas';
import { mediaRegistry } from '../../registry';
import { getDefaultFormat } from '../../registry/media/media.registered';

interface FormatSelectorProps {
  /** Selected media type */
  selectedMediaType: MediaType;
  /** Current format */
  format: TextFormat | DateFormat | NumberFormat | ColorFormat | ImageFormat;
  /** Format change handler */
  onFormatChange: (format: any) => void;
}

/**
 * Selector for choosing data format options
 */
export const FormatSelector: React.FC<FormatSelectorProps> = ({
  selectedMediaType,
  format,
  onFormatChange
}) => {
  // Get format options from media registry
  const formatOptions = mediaRegistry.getFormatOptions(selectedMediaType);
  
  if (formatOptions.length <= 1) {
    return null;
  }

  // Use default format if no format is set
  const currentFormat = format || getDefaultFormat(selectedMediaType);

  // helper: map format object -> select value
  const toValueFromFormat = (mt: MediaType, fmt: any): string => {
    switch (mt) {
      case 'text':
        return fmt?.kind ?? 'default';
      case 'date':
        return fmt?.pattern ?? 'DD.MM.YYYY';
      case 'int':
        if (fmt?.thousands) return 'thousands';
        if (fmt?.pad) return 'padded';
        return 'default';
      case 'float':
        if (fmt?.type) return fmt.type;
        if (fmt?.thousands && fmt?.decimals === 2 && fmt?.trimZeros) return 'thousands_trim';
        if (fmt?.thousands && fmt?.decimals === 2) return 'thousands_2';
        if (typeof fmt?.decimals === 'number') return `decimal${fmt.decimals}`;
        return 'decimal2';
      case 'image':
        return fmt?.fit ?? 'contain';
      case 'color':
        return 'default';
      default:
        return 'default';
    }
  };

  // helper: map select value -> format object
  const toFormatFromValue = (mt: MediaType, value: string): any => {
    switch (mt) {
      case 'text':
        return value === 'default' ? {} : { kind: value as TextFormat['kind'] };
      case 'date':
        return { pattern: value };
      case 'int':
        if (value === 'thousands') return { thousands: true } as NumberFormat;
        if (value === 'padded') return { pad: 2 } as NumberFormat;
        return {} as NumberFormat;
      case 'float':
        if (value === 'none') return { type: 'none' } as any;
        if (value === 'stripZeros') return { type: 'stripZeros' } as any;
        if (value === 'decimal2') return { type: 'decimal2' } as any;
        if (value === 'thousands_trim') return { type: 'thousands_trim' } as any;
        if (value === 'thousands_2') return { type: 'thousands_2' } as any;
        if (value === 'integer') return { type: 'integer', roundMode: 'round' } as any;
        if (value.startsWith('decimal')) {
          const d = Number(value.replace('decimal', '')) || 2;
          return { type: 'decimal2', decimals: d } as any;
        }
        return { type: 'decimal2' } as any;
      case 'image':
        return { fit: value as ImageFormat['fit'] } as ImageFormat;
      case 'color':
        return {} as ColorFormat;
      default:
        return {};
    }
  };

  return (
    <div>
      <Label className="text-xs text-gray-500 mb-1 block">Format</Label>
      <Select 
        value={toValueFromFormat(selectedMediaType, currentFormat)}
        onValueChange={(value) => onFormatChange(toFormatFromValue(selectedMediaType, value))}
      >
        <SelectTrigger className="h-6 text-xs">
          <SelectValue placeholder="Format wählen..." />
        </SelectTrigger>
        <SelectContent className="max-h-[200px] overflow-y-auto">
          {formatOptions.map((option) => (
            <SelectItem key={option.type} value={option.type} className="text-xs">
              <div className="flex items-center gap-2">
                <Settings className="w-3 h-3" />
                {option.label}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}